"use client";

import { useEffect, useState } from "react";
import { isPortalDisabled } from "@/helpers/portal";

export interface UseSessionUserResult {
  /** A portal session exists. Says nothing about whether the portal will let them in. */
  isAuthenticated: boolean;
  /**
   * A session exists *and* the address behind it has been confirmed — the same
   * test `middleware.ts` applies before letting anything into the portal.
   *
   * Kept separate from `isAuthenticated` because the two answer different
   * questions. "Do you already have an account?" (checkout's CTA) is not the
   * same as "can you open the portal right now?" (the header's), and a
   * supporter who has registered but not yet confirmed their address is a real
   * state where those answers differ.
   */
  isVerified: boolean;
  isLoading: boolean;
}

/**
 * Client-side check for an active supporter-portal session. Calls the
 * server-side session route (which reads the Auth0 cookie) once on mount; a
 * 401 is the expected "signed out" response, not an error.
 *
 * The route sits at `/api/session/` rather than under `/api/auth/` because
 * the header renders on every page, so this fires far more often than any
 * credential operation and should not share their rate limit budget.
 *
 * Used by the public header nav to swap the "Login" button for a "Portal" link
 * when the visitor can actually use one.
 *
 * Skips the request entirely while `NEXT_PUBLIC_DISABLE_PORTAL` is set: the
 * middleware 404s `/api/session/` along with the rest of the portal API, so the
 * fetch could only ever answer "signed out" — on every page, for every visitor.
 * Returning that answer directly also settles `isLoading` immediately, so the
 * callers that branch on it don't flicker through a loading state that cannot
 * resolve into anything else.
 */
export const useSessionUser = (): UseSessionUserResult => {
  const portalDisabled = isPortalDisabled();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(!portalDisabled);

  useEffect(() => {
    if (portalDisabled) {
      return;
    }

    let active = true;

    fetch("/api/session/", { headers: { Accept: "application/json" } })
      .then(async (res) => {
        if (!res.ok) {
          return { authenticated: false, verified: false };
        }
        // A body we can't read shouldn't promote someone to verified — fall
        // back to "signed in but not confirmed", which is the safe half.
        try {
          const body = await res.json();
          return { authenticated: true, verified: body?.user?.emailVerified === true };
        } catch {
          return { authenticated: true, verified: false };
        }
      })
      .catch(() => ({ authenticated: false, verified: false }))
      .then(({ authenticated, verified }) => {
        if (active) {
          setIsAuthenticated(authenticated);
          setIsVerified(verified);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [portalDisabled]);

  return { isAuthenticated, isLoading, isVerified };
};

export default useSessionUser;
